import { first, isEmpty } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import useDownloadMeta from "../../../../custom-hooks/useDownloadMeta";
import func from "@uikit/func";
import { fileType } from "./UploadFile";

export const UploadFileContext = React.createContext({});

export const UploadFileProvider = UploadFileContext.Provider;

/**
 * UploadFile 组件逻辑容器, 为了兼容之前的 audio 和 其他文件类型时公用的状态
 *
 * @param {*} args
 */
export function useUploadFile(args?: any) {
  const props: any = useContext(UploadFileContext);

  const [downloadMetaData] = useDownloadMeta();

  // state
  const [uploadStatus, setUploadStatus] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [fileList, setFileList] = useState<any[]>([]);

  // preview state
  const [previewState, setPreviewState] = useState({
    loading: false,
    previewURL: "",
  });

  // file data
  const fileData = first(fileList);
  const fileStatus = fileData?.status || "";
  const fileExt = fileType(fileData)?.toLocaleLowerCase();

  const getDownloadURL = (s3Key: string) => {
    return `${settings.API}/api/me/upload/im?url=${encodeURI(s3Key)}`;
  };

  const getLackVal = (tempVal: any) => {
    const reg = /\/([^\/]*)\b$/g;
    const arr = reg.exec(tempVal.s3Path);
    const name = arr?.[1] || "";
    return {
      name,
      s3Url: `https://s3.us-east-2.amazonaws.com/brain.ai/${tempVal.s3Path}`,
    };
  };

  const fetchDownloadMata = (tempVal: any, downloadURL: any) => {
    // 通用的文件下载流程
    setPreviewState(p => ({ ...p, loading: true }));
    downloadMetaData(downloadURL)
      .then(url => {
        setPreviewState({ loading: false, previewURL: url as string });
      })
      .finally(() => {
        setPreviewState(p => ({ ...p, loading: false }));
      });
  };

  const _onDownloadMata = (tempVal: any, downloadURL: any) => {
    if (previewState.loading) {
      return;
    }

    if (args?.onDownloadMata) {
      return args.onDownloadMata(tempVal, downloadURL);
    }

    fetchDownloadMata(tempVal, downloadURL);
  };

  useEffect(() => {
    if (func.isEmpty(fileData)) return;
    setUploadStatus(fileStatus || "uploading");

    args?.onUploading?.(props.dropUploadFileData);
    // // 本地数据上传显示loading
    // if(isAudioType(props.dropUploadFileData.fileData)) {
    //   setAudioLoading(true)
    // }

    if (fileStatus !== "success") return;

    args?.onUploaded?.(props.dropUploadFileData);
    // if(isAudioType(props.dropUploadFileData.fileData)) {
    //   const url = URL.createObjectURL(props.dropUploadFileData.fileData)
    //   URL.revokeObjectURL(props.dropUploadFileData.fileData)
    //   if(!util.isEmpty(audioRef.current)) {
    //     audioRef.current.src = url
    //   }
    //   setAudioLoading(false)
    // }

    props.setNodes((prevList: any[]) => {
      return prevList.map(x => {
        if (x.id === props.id && isEmpty(x.data.textAreaValue)) {
          return {
            ...x,
            data: {
              ...x.data,
              textAreaValue: {
                s3Url: fileData.s3Url,
                name: fileData.name,
                s3Path: fileData.s3Path,
                status: fileData.status,
                type: fileData.type,
                size: fileData.size,
              },
            },
          };
        }
        return x;
      });
    });
  }, [fileStatus]);

  // 本地拖拽的数据监听
  useEffect(() => {
    if (func.isEmpty(props.dropUploadFileData.fileData) || props.dropUploadFileData.nodeId !== props.id) return;

    setFileList([props.dropUploadFileData.fileData]);
  }, [props.dropUploadFileData]);

  // 远程保存的数据监听
  useEffect(() => {
    if (func.isEmpty(props.textAreaValue)) return;
    if (!func.isEmpty(props.dropUploadFileData)) {
      props.setNodes((prevList: any[]) => {
        return prevList.map(x => {
          if (x.id === props.id) {
            return {
              ...x,
              data: {
                ...x.data,
                dropUploadFileData: {},
              },
            };
          }
          return x;
        });
      });
      return;
    }
    let tempVal = props.textAreaValue;
    // 兼容旧数据解析
    if (func.isEmpty(tempVal.s3Url)) {
      const { name, s3Url } = getLackVal(tempVal);
      tempVal.name = name;
      tempVal.s3Url = s3Url;
      tempVal.size = 0;
    }
    setFileList([tempVal]);

    _onDownloadMata(tempVal, getDownloadURL(tempVal.s3Url));
    // const fileUrl = `${settings.API}/api/me/upload/im?url=${encodeURI(tempVal.s3Url)}`
    // // audio类型则下载资源
    // if(isAudioType(tempVal)) {
    //   setAudioLoading(true)
    //   downloadMetaData(fileUrl)
    //     .then((url) => {
    //       if(!util.isEmpty(audioRef.current)) {
    //         audioRef.current.src = url
    //       }
    //     })
    //     .catch(error => {
    //       util.customMsg({
    //         content: error?.message || `Load ${tempVal.name} failed`
    //       })
    //     })
    //     .finally(() => {
    //       setAudioLoading(false)
    //     })
    // }
  }, [props.textAreaValue]);

  return {
    uploadFileProps: props,

    fileData,
    fileStatus,
    fileExt,

    /**
     * 文件上传状态
     */
    uploadStatus,
    setUploadStatus,

    progressPercent,
    setProgressPercent,

    fileList,
    setFileList,
    previewState,
    setPreviewState,

    getDownloadURL,
    /**
     * 文件下载
     */
    fetchDownloadMata,
  };
}
